export interface EstimateResult {
  estimate: number | null;
  source: string;
  disclaimer: string;
  cached: boolean;
}

export async function fetchPropertyEstimate(address: string): Promise<EstimateResult> {
  const disclaimer = 'Estimated value – not an official appraisal.';
  if (!address || address.trim().length < 5) {
    return { estimate: null, source: 'None', disclaimer, cached: false };
  }

  const normalized = address.trim().toLowerCase();
  const cacheKey = `prop_est_${normalized}`;
  
  // Check localStorage cache first
  try {
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      const parsed = JSON.parse(cachedData);
      const now = Date.now();
      // TTL 7 days (7 * 24 * 60 * 60 * 1000 = 604800000 ms)
      if (now - parsed.timestamp < 604800000) {
        return {
          estimate: parsed.estimate,
          source: parsed.source,
          disclaimer,
          cached: true
        };
      }
    }
  } catch (err) {
    console.warn('Failed to read estimate cache:', err);
  }

  const apiKey = (import.meta.env.VITE_RENTCAST_API_KEY as string) || '';
  if (apiKey) {
    try {
      // Encode address for URL query
      const url = `https://api.rentcast.io/v1/avm/value?address=${encodeURIComponent(address)}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'X-Api-Key': apiKey
        }
      });
      if (res.ok) {
        const data = await res.json();
        if (data && typeof data.price === 'number') {
          const result = {
            estimate: data.price,
            source: 'RentCast AVM',
            disclaimer,
            cached: false
          };
          // Save to cache
          try {
            localStorage.setItem(cacheKey, JSON.stringify({
              ...result,
              timestamp: Date.now()
            }));
          } catch (e) {}
          return result;
        }
      }
    } catch (err) {
      console.error('RentCast API fetch error:', err);
    }
  }

  // Fallback: Generate a deterministic mock estimate based on address hash (so it is stable for the same address)
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    hash = normalized.charCodeAt(i) + ((hash << 5) - hash);
  }
  const minVal = 220000;
  const maxVal = 780000;
  const mockVal = minVal + Math.abs(hash % (maxVal - minVal));
  // Round to nearest thousand
  const estimate = Math.round(mockVal / 1000) * 1000;

  const result = {
    estimate,
    source: 'WholeScale AVM (Demo)',
    disclaimer,
    cached: false
  };

  // Cache fallback values too
  try {
    localStorage.setItem(cacheKey, JSON.stringify({
      ...result,
      timestamp: Date.now()
    }));
  } catch (e) {}

  return result;
}
